import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import ListingCard from "@/components/ListingCard";
import MapView from "@/components/MapView";
import { SlidersHorizontal, Map as MapIcon, LayoutGrid } from "lucide-react";

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [], conditions: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("grid"); // "grid" | "map"

  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [condition, setCondition] = useState(params.get("condition") || "");
  const [location, setLocation] = useState(params.get("location") || "");
  const [clearance, setClearance] = useState(params.get("clearance") === "true");
  const [verified, setVerified] = useState(params.get("verified") === "true");
  const [priceRange, setPriceRange] = useState([0, 5000]);

  useEffect(() => {
    api.get("/meta/categories").then(({ data }) => setMeta(data));
  }, []);

  const fetchWith = (overrides = {}) => {
    const filters = {
      q, category, condition, location, clearance, verified, priceRange,
      ...overrides,
    };
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.q) p.set("q", filters.q);
    if (filters.category) p.set("category", filters.category);
    if (filters.condition) p.set("condition", filters.condition);
    if (filters.location) p.set("location", filters.location);
    if (filters.clearance) p.set("clearance", "true");
    if (filters.verified) p.set("verified", "true");
    if (filters.priceRange[0] > 0) p.set("min_price", filters.priceRange[0]);
    if (filters.priceRange[1] < 5000) p.set("max_price", filters.priceRange[1]);
    p.set("limit", "40");
    setParams(p);
    return api.get(`/listings?${p.toString()}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); })
      .finally(() => setLoading(false));
  };
  const fetchListings = () => fetchWith();

  useEffect(() => { fetchListings(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8">
        {/* Sidebar filters */}
        <aside className="border border-border bg-white p-6 h-fit sticky top-24">
          <div className="flex items-center gap-2 mb-6">
            <SlidersHorizontal className="w-4 h-4 text-forest" />
            <div className="font-display text-lg tracking-tight">Filters</div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="overline text-muted-foreground block mb-2">Keyword</label>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. Herman Miller"
                data-testid="filter-q"
                className="rounded-none"
              />
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-2">Category</label>
              <Select value={category || "__any"} onValueChange={(v) => setCategory(v === "__any" ? "" : v)}>
                <SelectTrigger className="rounded-none" data-testid="filter-category"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="__any">Any category</SelectItem>
                  {meta.categories?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-2">Condition</label>
              <Select value={condition || "__any"} onValueChange={(v) => setCondition(v === "__any" ? "" : v)}>
                <SelectTrigger className="rounded-none" data-testid="filter-condition"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="__any">Any condition</SelectItem>
                  {meta.conditions?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-2">Location</label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="London, Manchester…"
                data-testid="filter-location"
                className="rounded-none"
              />
            </div>

            <div>
              <label className="overline text-muted-foreground block mb-2">
                Price £{priceRange[0]} – £{priceRange[1]}{priceRange[1] === 5000 ? "+" : ""}
              </label>
              <Slider
                value={priceRange}
                onValueChange={setPriceRange}
                min={0}
                max={5000}
                step={50}
                data-testid="filter-price"
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={clearance}
                onCheckedChange={(v) => setClearance(!!v)}
                data-testid="filter-clearance"
              />
              <span className="text-sm">Clearance sales only</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox
                checked={verified}
                onCheckedChange={(v) => {
                  const nv = !!v;
                  setVerified(nv);
                  // Auto-apply the verified filter immediately for a snappier UX
                  fetchWith({ verified: nv });
                }}
                data-testid="filter-verified"
              />
              <span className="text-sm flex items-center gap-1.5">Verified sellers only <span className="bg-forest text-white overline text-[9px] px-1.5 py-0.5">✓</span></span>
            </label>

            <Button
              onClick={fetchListings}
              data-testid="filter-apply"
              className="w-full rounded-none bg-forest hover:bg-forest-800 text-white h-11"
            >
              Apply filters
            </Button>
            <button
              onClick={() => {
                setQ(""); setCategory(""); setCondition(""); setLocation("");
                setClearance(false); setVerified(false); setPriceRange([0, 5000]);
                setTimeout(fetchListings, 0);
              }}
              className="w-full text-xs text-muted-foreground hover:text-forest transition-colors duration-200 overline"
              data-testid="filter-reset"
            >
              Reset
            </button>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline text-safety">Marketplace</div>
              <h1 className="mt-2 font-display font-light text-3xl lg:text-4xl tracking-tight text-forest">
                {total} listing{total === 1 ? "" : "s"}
              </h1>
            </div>
            <div className="flex border border-border">
              <button
                onClick={() => setView("grid")}
                data-testid="view-grid"
                className={`px-4 h-10 flex items-center gap-2 text-xs overline transition-colors duration-200 ${view === "grid" ? "bg-forest text-white" : "bg-white hover:bg-sage"}`}
              >
                <LayoutGrid className="w-4 h-4" /> Grid
              </button>
              <button
                onClick={() => setView("map")}
                data-testid="view-map"
                className={`px-4 h-10 flex items-center gap-2 text-xs overline transition-colors duration-200 ${view === "map" ? "bg-forest text-white" : "bg-white hover:bg-sage"}`}
              >
                <MapIcon className="w-4 h-4" /> Map
              </button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-sage animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="border border-border bg-white p-16 text-center">
              <div className="overline text-muted-foreground mb-3">No matches yet</div>
              <div className="font-display text-2xl tracking-tight text-forest">Try widening your filters</div>
              <p className="mt-2 text-sm text-muted-foreground">Nothing matches your search. Try broader keywords or clear filters.</p>
            </div>
          ) : view === "map" ? (
            <MapView listings={items} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((l) => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
